<template>
  <div class="flex flex-col w-full bg-gray-100">
    <slot name="breadcrumbs" />

    <div class="lg:flex lg:grow">
      <SideNav v-if="desktopLarge" :breadcrumb="breadcrumb" :is-disabled="isEmpty && !appliedFilters.length" />

      <div class="box-border w-full overflow-hidden lg:shrink">
        <slot name="category-hero" />
        <div class="flex flex-col p-4 pt-4 md:p-8">
          <div class="md:flex md:justify-between">
            <div v-if="!isEmpty || appliedFilters.length" class="flex flex-col pb-4 mr-4 space-y-2 md:pb-0 lg:max-w-xl">
              <slot name="title-desc" />
            </div>

            <div class="flex items-center text-xs text-gray-800 lg:self-end lg:justify-between">
              <div class="lg:hidden">
                <CIconButton
                  class="box-border flex mr-3 min-w-[160px]"
                  size="small"
                  :variant="variantName"
                  :name="iconName"
                  :icon-classes="`text-${iconColor}`"
                  :active="isMobileSideNavVisible"
                  @click="toggleMobileSideNav()"
                >
                  <template v-if="!isMobileSideNavVisible" #right>
                    <span class="c-icon-button__text-right">
                      {{ filterButtonText }}
                    </span>
                  </template>
                  <template v-if="isMobileSideNavVisible">
                    <span class="c-icon-button__text-left">
                      {{ filterButtonText }}
                    </span>
                  </template>
                </CIconButton>
              </div>
              <hr />
              <div class="flex items-center gap-2">
                <slot name="count" />
                <slot v-if="desktopLarge" name="sort" class="mr-2" />
                <slot v-if="desktopLarge" name="per-page" />
              </div>
            </div>
          </div>
          <slot name="line" />
        </div>

        <div :class="{ 'flex flex-row': !desktopLarge, 'justify-center': isEmpty }">
          <client-only>
            <SideNav
              v-if="!desktopLarge"
              :breadcrumb="breadcrumb"
              :is-disabled="isEmpty && !appliedFilters.length"
              class="side-nav-mobile"
              :class="{ visible: isMobileSideNavVisible }"
            >
              <template #sort>
                <slot name="sort" />
              </template>
            </SideNav>
          </client-only>
          <div v-if="!isEmpty" class="px-4 min-w-[calc(100%-32px)] md:px-8" data-test="products">
            <ProductListingPageAppliedFilters v-if="appliedFilters.length" class="mb-6" />
            <slot name="products" />
            <slot name="loadmore" />
          </div>
          <div v-else class="flex flex-col items-center px-4 md:px-8">
            <ProductListingPageAppliedFilters v-if="appliedFilters.length" class="w-full" />
            <slot name="empty" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useProductStore } from '@/stores';

type Props = {
  breadcrumb?: any;
  isEmpty?: boolean;
};

withDefaults(defineProps<Props>(), {
  breadcrumb: () => ({}),
  isEmpty: false,
});

const { mq } = useMediaQueries();
const { t } = useI18n();
const { appliedFilters } = storeToRefs(useProductStore());

const isMobileSideNavVisible = ref(false);

const filterButtonText = computed(() => (isMobileSideNavVisible.value ? t('close_filter') : t('sort_and_filter')));
const iconName = computed(() => (isMobileSideNavVisible.value ? 'close' : 'chevron_left'));
const variantName = computed(() => (isMobileSideNavVisible.value ? 'secondary--clicked' : 'secondary'));
const iconColor = computed(() => {
  if (iconName.value === 'close') return 'white';
  return undefined;
});
const desktopLarge = computed(() => !(mq.value.xsmall || mq.value.small || mq.value.medium));

const toggleMobileSideNav = () => {
  isMobileSideNavVisible.value = !isMobileSideNavVisible.value;
};
</script>

<script lang="ts">
export default defineComponent({
  name: 'ProductListingPageLayout',
});
</script>

<style lang="scss" scoped>
$mobile-side-nav-width: 288px;

.side-nav-mobile {
  transition: margin 1.4s;
  margin-left: -$mobile-side-nav-width;

  &.visible {
    @apply m-0;
  }
}
</style>
